import React, { useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator, RefreshControl } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Animated, { FadeInDown } from 'react-native-reanimated';
import {
  Activity,
  Clock,
  Zap,
  Timer,
  Search,
  FileText,
  Handshake,
  UserX,
  ShieldX,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAdminRole, hasPermission } from '@/lib/admin';
import { useProductDashboard } from '@/lib/admin/use-product-dashboard';
import type { TimeRange, FeatureAdoptionRow, ProductDashboardData } from '@/lib/admin/use-product-dashboard';
import { isWeb } from '@/lib/platform';
import { AppLayout } from '@/components/AppLayout';
import { WebCard, WebGrid } from '@/components/web/WebAppShell';
import { StatCardEnhanced } from '@/components/admin/StatCardEnhanced';
import { TimeRangeSelector, SegmentedToggle } from '@/components/admin/TimeRangeSelector';
import { SortableTable, AdoptionBar, TrendBadge } from '@/components/admin/SortableTable';
import type { TableColumn } from '@/components/admin/SortableTable';
import { JourneyFlow } from '@/components/admin/JourneyFlow';
import { GroupedBarChart } from '@/components/admin/GroupedBarChart';
import { AreaChart } from '@/components/admin/AreaChart';
import { MiniBarChart } from '@/components/admin/MiniBarChart';

export default function ProductAnalyticsScreen() {
  const colors = useThemeColors();
  const { data: adminRole, isLoading: roleLoading } = useAdminRole();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [adoptionTab, setAdoptionTab] = useState('Startup');
  const [journeyTab, setJourneyTab] = useState('Startup');

  const { data, isLoading, isRefetching, refetch } = useProductDashboard(timeRange);

  // Loading
  if (roleLoading || isLoading) {
    return (
      <AppLayout
        title="Product Analytics"
        breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Product Analytics' }]}
      >
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <ActivityIndicator size="large" color={colors.primary[600]} />
        </View>
      </AppLayout>
    );
  }

  // Permission gate
  if (!hasPermission(adminRole, 'dashboard_engagement')) {
    return (
      <AppLayout
        title="Product Analytics"
        breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Product Analytics' }]}
      >
        <View className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.surface.secondary }}>
          <ShieldX size={48} color={colors.text.tertiary} />
          <Text style={{ color: colors.text.primary }} className="text-xl font-bold mt-4 text-center">
            Access Restricted
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm text-center mt-2">
            Your admin role does not have permission to view product analytics.
          </Text>
        </View>
      </AppLayout>
    );
  }

  const engagement = data?.engagement;
  const dauMauColor = (engagement?.dauMauRatio ?? 0) >= 20 ? '#22C55E' : (engagement?.dauMauRatio ?? 0) >= 10 ? '#F59E0B' : '#EF4444';

  // Feature adoption filtered by tab
  const filteredAdoption = (data?.featureAdoption ?? []).filter(
    (r) => r.userType === (adoptionTab === 'Startup' ? 'startup' : 'investor')
  );

  // Journey data based on tab
  const journeyData = journeyTab === 'Startup' ? data?.startupJourney : data?.investorJourney;

  // Churn curve as area chart data
  const churnAreaData = (data?.churnAnalysis.churnCurve ?? []).map((d) => ({
    date: `D${d.day}`,
    values: [d.startupRetention, d.investorRetention],
  }));

  // Feature adoption table columns
  const adoptionColumns: TableColumn<FeatureAdoptionRow>[] = [
    {
      key: 'feature',
      label: 'Feature',
      width: 120,
      getValue: (r) => r.feature,
      render: (r) => (
        <Text style={{ color: colors.text.primary }} className="text-xs font-medium" numberOfLines={1}>
          {r.feature}
        </Text>
      ),
    },
    {
      key: 'uniqueUsers7d',
      label: '7d Users',
      width: 68,
      align: 'right',
      getValue: (r) => r.uniqueUsers7d,
      render: (r) => (
        <Text style={{ color: colors.text.primary }} className="text-xs font-semibold">{r.uniqueUsers7d}</Text>
      ),
    },
    {
      key: 'uniqueUsers30d',
      label: '30d Users',
      width: 72,
      align: 'right',
      getValue: (r) => r.uniqueUsers30d,
      render: (r) => (
        <Text style={{ color: colors.text.secondary }} className="text-xs">{r.uniqueUsers30d}</Text>
      ),
    },
    {
      key: 'totalUses7d',
      label: '7d Uses',
      width: 64,
      align: 'right',
      getValue: (r) => r.totalUses7d,
      render: (r) => (
        <Text style={{ color: colors.text.primary }} className="text-xs font-semibold">{r.totalUses7d}</Text>
      ),
    },
    {
      key: 'adoptionRate',
      label: 'Adoption',
      width: 110,
      getValue: (r) => r.adoptionRate,
      render: (r) => {
        const barColor = r.adoptionRate >= 60 ? '#22C55E' : r.adoptionRate >= 35 ? '#F59E0B' : '#EF4444';
        return <AdoptionBar value={r.adoptionRate} color={barColor} />;
      },
    },
    {
      key: 'trend',
      label: 'Trend',
      width: 56,
      align: 'right',
      getValue: (r) => r.trend,
      render: (r) => <TrendBadge value={r.trend} />,
    },
  ];

  // Max for search filter bars
  const maxFilterUses = Math.max(...(data?.searchAnalytics.topFilters ?? []).map((f) => f.uses), 1);
  // Max for content section views
  const maxSectionViews = Math.max(...(data?.contentEngagement.topSections ?? []).map((s) => s.views), 1);
  // Max for last action bars
  const maxActionPct = Math.max(...(data?.churnAnalysis.lastActionBeforeChurn ?? []).map((a) => a.percentage), 1);

  if (!isWeb) {
    return (
      <MobileProductAnalytics
        data={data}
        isRefetching={isRefetching}
        refetch={refetch}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        adoptionTab={adoptionTab}
        setAdoptionTab={setAdoptionTab}
        journeyTab={journeyTab}
        setJourneyTab={setJourneyTab}
        engagement={engagement}
        dauMauColor={dauMauColor}
        filteredAdoption={filteredAdoption}
        journeyData={journeyData}
        churnAreaData={churnAreaData}
        maxFilterUses={maxFilterUses}
        maxSectionViews={maxSectionViews}
        maxActionPct={maxActionPct}
        colors={colors}
      />
    );
  }

  return (
    <AppLayout
      title="Product Analytics"
      breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'Product Analytics' }]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['bottom']}>
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 40 }}
          refreshControl={
            <RefreshControl refreshing={isRefetching} onRefresh={() => refetch()} tintColor={colors.primary[600]} />
          }
        >
          {/* Time Range Selector */}
          <Animated.View entering={FadeInDown.delay(50).duration(400)} className="mb-6">
            <WebCard>
              <View className="flex-row items-center justify-between">
                <View>
                  <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                    Product & Engagement
                  </Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5">
                    Is the product working? What drives engagement?
                  </Text>
                </View>
                <View style={{ width: 200 }}>
                  <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
                </View>
              </View>
            </WebCard>
          </Animated.View>

          {/* Section 1: Engagement Overview Cards */}
          <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
            <WebGrid columns={{ mobile: 2, tablet: 2, desktop: 4 }} gap={16}>
              <StatCardEnhanced
                icon={<Activity size={22} color={dauMauColor} />}
                iconBg={`${dauMauColor}12`}
                label="DAU/MAU Ratio"
                value={`${(engagement?.dauMauRatio ?? 0).toFixed(1)}%`}
                delta={engagement?.dauMauTrend}
                deltaLabel="vs prior"
              />
              <StatCardEnhanced
                icon={<Clock size={22} color="#3B82F6" />}
                iconBg="#3B82F612"
                label="Avg Session Duration"
                value={`${(engagement?.avgSessionDuration ?? 0).toFixed(1)}m`}
                delta={engagement?.sessionDurationTrend}
                deltaLabel="vs prior"
              />
              <StatCardEnhanced
                icon={<Zap size={22} color="#8B5CF6" />}
                iconBg="#8B5CF612"
                label="Events / Session"
                value={(engagement?.avgEventsPerSession ?? 0).toFixed(1)}
                delta={engagement?.eventsPerSessionTrend}
                deltaLabel="vs prior"
              />
              <StatCardEnhanced
                icon={<Timer size={22} color="#F59E0B" />}
                iconBg="#F59E0B12"
                label="Time to First Value"
                value={`${(engagement?.timeToFirstValue ?? 0).toFixed(1)}d`}
              />
            </WebGrid>
          </Animated.View>

          {/* Duration split callout */}
          <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-6">
            <WebCard>
              {/* This branch is WEB-ONLY (native returns MobileProductAnalytics
                  above), so the constraint is a narrow BROWSER window, not a
                  phone. Four stats at ~105pt of label each still overflow a
                  ~375pt viewport, and the row previously had neither wrapping
                  nor scrolling to absorb it.

                  Wrapping is driven by width via flexBasis, not by a platform
                  flag — an `isWeb` check would be constantly true here and
                  change nothing. The dividers are gone: a vertical hairline
                  between items that may wrap onto separate lines separates
                  nothing, and `justify-around` already spaces them. */}
              <View className="flex-row flex-wrap items-center justify-around gap-y-4">
                <DurationSplit label="Startup Avg" value={engagement?.avgSessionDurationByType.startup ?? 0} unit="min" colors={colors} />
                <DurationSplit label="Investor Avg" value={engagement?.avgSessionDurationByType.investor ?? 0} unit="min" colors={colors} />
                <DurationSplit label="Startup TTFV" value={engagement?.timeToFirstValueByType.startup ?? 0} unit="days" colors={colors} />
                <DurationSplit label="Investor TTFV" value={engagement?.timeToFirstValueByType.investor ?? 0} unit="days" colors={colors} />
              </View>
            </WebCard>
          </Animated.View>

          {/* Section 2: Feature Adoption Matrix */}
          <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mb-6">
            <WebCard>
              <View className="flex-row items-center justify-between mb-4">
                <SectionHeader title="Feature Adoption Matrix" subtitle="Usage by feature and time period" colors={colors} />
                <View style={{ width: 160 }}>
                  <SegmentedToggle options={['Startup', 'Investor']} value={adoptionTab} onChange={setAdoptionTab} />
                </View>
              </View>
              <SortableTable
                columns={adoptionColumns}
                data={filteredAdoption}
                keyExtractor={(r) => `${r.feature}-${r.userType}`}
                defaultSortKey="adoptionRate"
              />
            </WebCard>
          </Animated.View>

          {/* Section 3: User Journey Maps */}
          <Animated.View entering={FadeInDown.delay(300).duration(500)} className="mb-6">
            <WebCard>
              <View className="flex-row items-center justify-between mb-4">
                <SectionHeader title="User Journey" subtitle="Conversion through key milestones" colors={colors} />
                <View style={{ width: 160 }}>
                  <SegmentedToggle options={['Startup', 'Investor']} value={journeyTab} onChange={setJourneyTab} />
                </View>
              </View>
              {journeyData && (
                <JourneyFlow steps={journeyData.steps} totalStart={journeyData.totalStart} />
              )}
            </WebCard>
          </Animated.View>

          {/* Section 4: Session Depth Analysis */}
          <Animated.View entering={FadeInDown.delay(400).duration(500)} className="mb-6">
            <WebGrid columns={{ mobile: 1, tablet: 1, desktop: 2 }} gap={16}>
              <WebCard>
                <SectionHeader title="Session Duration Distribution" subtitle="Buckets by user type" colors={colors} />
                <GroupedBarChart
                  data={(data?.sessionDepth.buckets ?? []).map((b) => ({
                    label: b.label,
                    values: [b.startups, b.investors],
                  }))}
                  seriesColors={['#0D9488', '#3B82F6']}
                  seriesLabels={['Startups', 'Investors']}
                  height={200}
                />
                {/* Insight callout */}
                {data?.sessionDepth.insightText && (
                  <View
                    className="flex-row items-start gap-2 mt-3 px-3 py-2.5 rounded-lg"
                    style={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}
                  >
                    <AlertTriangle size={14} color="#D97706" style={{ marginTop: 1 }} />
                    <Text style={{ color: '#92400E' }} className="text-xs flex-1 leading-4">
                      {data.sessionDepth.insightText}
                    </Text>
                  </View>
                )}
              </WebCard>

              <WebCard>
                <SectionHeader title="Sessions per Week" subtitle="By user type over last 4 weeks" colors={colors} />
                <GroupedBarChart
                  data={(data?.sessionDepth.sessionsPerWeek ?? []).map((w) => ({
                    label: w.week,
                    values: [w.startups, w.investors],
                  }))}
                  seriesColors={['#0D9488', '#3B82F6']}
                  seriesLabels={['Startups', 'Investors']}
                  height={200}
                />
              </WebCard>
            </WebGrid>
          </Animated.View>

          {/* Section 5: Search & Discovery Analytics */}
          <Animated.View entering={FadeInDown.delay(450).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader title="Search & Discovery" subtitle="How investors find startups" colors={colors} />

              {/* KPI row */}
              <View className="mb-4">
                <WebGrid columns={{ mobile: 3, tablet: 3, desktop: 3 }} gap={12}>
                  <MiniKpi
                    icon={<Search size={14} color="#3B82F6" />}
                    label="Search → View"
                    value={`${(data?.searchAnalytics.searchToViewRate ?? 0).toFixed(1)}%`}
                    colors={colors}
                  />
                  <MiniKpi
                    icon={<AlertTriangle size={14} color="#EF4444" />}
                    label="Zero Results"
                    value={`${(data?.searchAnalytics.zeroResultsRate ?? 0).toFixed(1)}%`}
                    colors={colors}
                  />
                  <MiniKpi
                    icon={<Search size={14} color="#22C55E" />}
                    label="Top Category"
                    value={data?.searchAnalytics.topCategories[0]?.category ?? '—'}
                    colors={colors}
                  />
                </WebGrid>
              </View>

              <WebGrid columns={{ mobile: 1, tablet: 1, desktop: 2 }} gap={16}>
                {/* Top filters */}
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
                    Top Search Filters
                  </Text>
                  <View className="gap-2">
                    {(data?.searchAnalytics.topFilters ?? []).slice(0, 8).map((f, i) => (
                      <View key={`filter-${i}`} className="flex-row items-center gap-2">
                        <Text style={{ color: colors.text.secondary, width: 88 }} className="text-xs" numberOfLines={1}>
                          {f.filter}
                        </Text>
                        <View className="flex-1 h-4 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                          <View
                            className="h-full rounded"
                            style={{ width: `${(f.uses / maxFilterUses) * 100}%`, backgroundColor: '#3B82F630' }}
                          />
                        </View>
                        <Text style={{ color: colors.text.primary, width: 36 }} className="text-[10px] font-bold text-right">
                          {f.uses}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                {/* Top categories */}
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
                    Most Viewed Categories
                  </Text>
                  <View className="gap-2">
                    {(data?.searchAnalytics.topCategories ?? []).map((c, i) => (
                      <View key={`category-${i}`} className="flex-row items-center gap-2">
                        <Text style={{ color: colors.text.secondary, width: 88 }} className="text-xs" numberOfLines={1}>
                          {c.category}
                        </Text>
                        <View className="flex-1 h-4 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                          <View
                            className="h-full rounded"
                            style={{
                              width: `${(c.views / (data?.searchAnalytics.topCategories[0]?.views ?? 1)) * 100}%`,
                              backgroundColor: '#8B5CF630',
                            }}
                          />
                        </View>
                        <Text style={{ color: colors.text.primary, width: 36 }} className="text-[10px] font-bold text-right">
                          {c.views}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </WebGrid>
            </WebCard>
          </Animated.View>

          {/* Section 6: Content Engagement */}
          <Animated.View entering={FadeInDown.delay(500).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader title="Content Engagement" subtitle="Startup profile and document metrics" colors={colors} />

              <WebGrid columns={{ mobile: 1, tablet: 1, desktop: 2 }} gap={16}>
                {/* Profile completeness distribution */}
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
                    Profile Completeness Distribution
                  </Text>
                  <MiniBarChart
                    data={(data?.contentEngagement.completenessDistribution ?? []).map((b) => ({
                      label: b.bucket,
                      value: b.count,
                    }))}
                    color="#0D9488"
                    height={120}
                  />
                  {/* Correlation stat */}
                  <View
                    className="flex-row items-center gap-2 mt-3 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: 'rgba(34, 197, 94, 0.08)' }}
                  >
                    <TrendingUp size={12} color="#16A34A" />
                    <Text style={{ color: '#166534' }} className="text-[10px]">
                      Startups with {'>'}78% completeness get {data?.contentEngagement.completenessVsViews.avgViews.toFixed(1)}x more views
                    </Text>
                  </View>
                </View>

                {/* Most viewed sections */}
                <View>
                  <View className="flex-row items-center justify-between mb-2">
                    <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold">
                      Most Viewed Sections
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-[10px]">
                      Doc upload rate: {(data?.contentEngagement.documentUploadRate ?? 0).toFixed(1)}%
                    </Text>
                  </View>
                  <View className="gap-2">
                    {(data?.contentEngagement.topSections ?? []).map((s, i) => (
                      <View key={`section-${i}`} className="flex-row items-center gap-2">
                        <Text style={{ color: colors.text.secondary, width: 100 }} className="text-xs" numberOfLines={1}>
                          {s.section}
                        </Text>
                        <View className="flex-1 h-4 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                          <View
                            className="h-full rounded"
                            style={{ width: `${(s.views / maxSectionViews) * 100}%`, backgroundColor: '#0D948830' }}
                          />
                        </View>
                        <Text style={{ color: colors.text.primary, width: 36 }} className="text-[10px] font-bold text-right">
                          {s.views}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </WebGrid>
            </WebCard>
          </Animated.View>

          {/* Section 7: Matching Quality */}
          <Animated.View entering={FadeInDown.delay(550).duration(500)} className="mb-6">
            <WebCard>
              <SectionHeader title="Matching Quality" subtitle="Are connections happening and working?" colors={colors} />

              <View className="mb-4">
                <WebGrid columns={{ mobile: 2, tablet: 2, desktop: 4 }} gap={12}>
                  <StatCardEnhanced
                    icon={<Handshake size={18} color="#0D9488" />}
                    iconBg="#0D948812"
                    label="Matches/Investor/Wk"
                    value={(data?.matchingQuality.matchesPerInvestorPerWeek ?? 0).toFixed(1)}
                  />
                  <StatCardEnhanced
                    icon={<Zap size={18} color="#22C55E" />}
                    iconBg="#22C55E12"
                    label="Response Rate"
                    value={`${(data?.matchingQuality.responseRate ?? 0).toFixed(1)}%`}
                  />
                  <StatCardEnhanced
                    icon={<Clock size={18} color="#3B82F6" />}
                    iconBg="#3B82F612"
                    label="Avg Response Time"
                    value={`${(data?.matchingQuality.avgResponseTimeHours ?? 0).toFixed(0)}h`}
                  />
                  <StatCardEnhanced
                    icon={<Activity size={18} color="#8B5CF6" />}
                    iconBg="#8B5CF612"
                    label="Connection Quality"
                    value={`${data?.matchingQuality.connectionQualityScore ?? 0}/100`}
                  />
                </WebGrid>
              </View>

              {/* Unmatched supply/demand callouts */}
              <WebGrid columns={{ mobile: 1, tablet: 2, desktop: 2 }} gap={12}>
                <View
                  className="flex-row items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}
                >
                  <UserX size={20} color="#D97706" />
                  <View className="flex-1">
                    <Text style={{ color: '#92400E' }} className="text-xs font-bold">
                      {data?.matchingQuality.unmatchedStartups ?? 0} Unmatched Startups
                    </Text>
                    <Text style={{ color: '#A16207' }} className="text-[10px]">
                      Complete profiles with 0 views in 30d
                    </Text>
                  </View>
                </View>
                <View
                  className="flex-row items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)' }}
                >
                  <Search size={20} color="#DC2626" />
                  <View className="flex-1">
                    <Text style={{ color: '#991B1B' }} className="text-xs font-bold">
                      {data?.matchingQuality.unmatchedInvestors ?? 0} Unmatched Investors
                    </Text>
                    <Text style={{ color: '#B91C1C' }} className="text-[10px]">
                      Searched but shortlisted nothing
                    </Text>
                  </View>
                </View>
              </WebGrid>
            </WebCard>
          </Animated.View>

          {/* Section 8: Drop-off & Churn Analysis */}
          <Animated.View entering={FadeInDown.delay(600).duration(500)}>
            <WebCard>
              <SectionHeader title="Drop-off & Churn" subtitle="When and why users leave" colors={colors} />

              <WebGrid columns={{ mobile: 1, tablet: 1, desktop: 2 }} gap={16}>
                {/* Churn curve */}
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
                    Retention Curve (% remaining)
                  </Text>
                  <AreaChart
                    data={churnAreaData}
                    colors={['#0D9488', '#3B82F6']}
                    labels={['Startups', 'Investors']}
                    height={200}
                  />
                </View>

                {/* Last action before churn */}
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
                    Last Action Before Churn
                  </Text>
                  <View className="gap-2">
                    {(data?.churnAnalysis.lastActionBeforeChurn ?? []).map((a, i) => (
                      <View key={`action-${i}`} className="flex-row items-center gap-2">
                        <Text style={{ color: colors.text.secondary, width: 100 }} className="text-xs" numberOfLines={1}>
                          {a.action}
                        </Text>
                        <View className="flex-1 h-4 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                          <View
                            className="h-full rounded"
                            style={{ width: `${(a.percentage / maxActionPct) * 100}%`, backgroundColor: '#EF444430' }}
                          />
                        </View>
                        <Text style={{ color: colors.text.primary, width: 32 }} className="text-[10px] font-bold text-right">
                          {a.percentage}%
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </WebGrid>

              {/* Reactivation rates */}
              <View className="mt-4">
                <WebGrid columns={{ mobile: 2, tablet: 3, desktop: 3 }} gap={12}>
                  <StatCardEnhanced
                    icon={<RefreshCw size={18} color="#22C55E" />}
                    iconBg="#22C55E12"
                    label="Reactivation Rate"
                    value={`${(data?.churnAnalysis.reactivationRate ?? 0).toFixed(1)}%`}
                  />
                  <StatCardEnhanced
                    icon={<TrendingUp size={18} color="#0D9488" />}
                    iconBg="#0D948812"
                    label="Startup Reactivation"
                    value={`${(data?.churnAnalysis.reactivationRateByType.startup ?? 0).toFixed(1)}%`}
                  />
                  <StatCardEnhanced
                    icon={<TrendingDown size={18} color="#3B82F6" />}
                    iconBg="#3B82F612"
                    label="Investor Reactivation"
                    value={`${(data?.churnAnalysis.reactivationRateByType.investor ?? 0).toFixed(1)}%`}
                  />
                </WebGrid>
              </View>
            </WebCard>
          </Animated.View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}

// ---- Mobile layout ----

type MobileProductAnalyticsProps = {
  data: ProductDashboardData | undefined;
  isRefetching: boolean;
  refetch: () => void;
  timeRange: TimeRange;
  setTimeRange: (v: TimeRange) => void;
  adoptionTab: string;
  setAdoptionTab: (v: string) => void;
  journeyTab: string;
  setJourneyTab: (v: string) => void;
  engagement: ProductDashboardData['engagement'] | undefined;
  dauMauColor: string;
  filteredAdoption: FeatureAdoptionRow[];
  journeyData: ProductDashboardData['startupJourney'] | undefined;
  churnAreaData: { date: string; values: number[] }[];
  maxFilterUses: number;
  maxSectionViews: number;
  maxActionPct: number;
  colors: ReturnType<typeof useThemeColors>;
};

function MobileProductAnalytics({
  data,
  isRefetching,
  refetch,
  timeRange,
  setTimeRange,
  adoptionTab,
  setAdoptionTab,
  journeyTab,
  setJourneyTab,
  engagement,
  dauMauColor,
  filteredAdoption,
  journeyData,
  churnAreaData,
  maxFilterUses,
  maxSectionViews,
  maxActionPct,
  colors,
}: MobileProductAnalyticsProps) {
  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['top', 'bottom']}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 40, paddingHorizontal: 16 }}
        refreshControl={
          <RefreshControl refreshing={isRefetching} onRefresh={() => refetch()} tintColor={colors.primary[600]} />
        }
      >
        {/* Time range selector — full width */}
        <Animated.View entering={FadeInDown.delay(50).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <Text style={{ color: colors.text.primary }} className="text-base font-bold mb-1">
              Product & Engagement
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-xs mb-3">
              Is the product working? What drives engagement?
            </Text>
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          </MobCard>
        </Animated.View>

        {/* Engagement KPIs: 2x2 compact pill grid */}
        <Animated.View entering={FadeInDown.delay(100).duration(400)} className="mb-4">
          <View className="flex-row flex-wrap gap-3">
            <MobKpiPill
              icon={<Activity size={14} color={dauMauColor} />}
              label="DAU/MAU"
              value={`${(engagement?.dauMauRatio ?? 0).toFixed(1)}%`}
              accentColor={dauMauColor}
              colors={colors}
            />
            <MobKpiPill
              icon={<Clock size={14} color="#3B82F6" />}
              label="Session Duration"
              value={`${(engagement?.avgSessionDuration ?? 0).toFixed(1)}m`}
              accentColor="#3B82F6"
              colors={colors}
            />
            <MobKpiPill
              icon={<Zap size={14} color="#8B5CF6" />}
              label="Events/Session"
              value={(engagement?.avgEventsPerSession ?? 0).toFixed(1)}
              accentColor="#8B5CF6"
              colors={colors}
            />
            <MobKpiPill
              icon={<Timer size={14} color="#F59E0B" />}
              label="Time to Value"
              value={`${(engagement?.timeToFirstValue ?? 0).toFixed(1)}d`}
              accentColor="#F59E0B"
              colors={colors}
            />
          </View>
        </Animated.View>

        {/* Duration split: 2 per row */}
        <Animated.View entering={FadeInDown.delay(130).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <MobSectionLabel title="Session Duration Breakdown" colors={colors} />
            <View className="flex-row gap-3">
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                  {(engagement?.avgSessionDurationByType.startup ?? 0).toFixed(1)}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Startup (min)
                </Text>
              </View>
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                  {(engagement?.avgSessionDurationByType.investor ?? 0).toFixed(1)}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Investor (min)
                </Text>
              </View>
            </View>
            <View className="flex-row gap-3 mt-2">
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                  {(engagement?.timeToFirstValueByType.startup ?? 0).toFixed(1)}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Startup TTFV (d)
                </Text>
              </View>
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
                  {(engagement?.timeToFirstValueByType.investor ?? 0).toFixed(1)}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Investor TTFV (d)
                </Text>
              </View>
            </View>
          </MobCard>
        </Animated.View>

        {/* Feature Adoption: simple list cards, no sortable table */}
        <Animated.View entering={FadeInDown.delay(160).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <View className="flex-row items-center justify-between mb-3">
              <MobSectionLabel title="Feature Adoption" colors={colors} />
              <View style={{ width: 140 }}>
                <SegmentedToggle options={['Startup', 'Investor']} value={adoptionTab} onChange={setAdoptionTab} />
              </View>
            </View>
            <View className="gap-2">
              {filteredAdoption.map((row, i) => {
                const barColor = row.adoptionRate >= 60 ? '#22C55E' : row.adoptionRate >= 35 ? '#F59E0B' : '#EF4444';
                return (
                  <View key={`adoption-${i}`} className="rounded-xl px-3 py-2.5" style={{ backgroundColor: colors.surface.secondary }}>
                    <View className="flex-row items-center justify-between mb-1.5">
                      <Text style={{ color: colors.text.primary }} className="text-xs font-medium flex-1" numberOfLines={1}>
                        {row.feature}
                      </Text>
                      <Text style={{ color: barColor }} className="text-xs font-bold ml-2">
                        {row.adoptionRate}%
                      </Text>
                    </View>
                    <View className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${barColor}20` }}>
                      <View
                        className="h-full rounded-full"
                        style={{ width: `${row.adoptionRate}%`, backgroundColor: barColor }}
                      />
                    </View>
                  </View>
                );
              })}
            </View>
          </MobCard>
        </Animated.View>

        {/* User Journey: full width */}
        <Animated.View entering={FadeInDown.delay(200).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <View className="flex-row items-center justify-between mb-3">
              <MobSectionLabel title="User Journey" colors={colors} />
              <View style={{ width: 140 }}>
                <SegmentedToggle options={['Startup', 'Investor']} value={journeyTab} onChange={setJourneyTab} />
              </View>
            </View>
            {journeyData && (
              <JourneyFlow steps={journeyData.steps} totalStart={journeyData.totalStart} />
            )}
          </MobCard>
        </Animated.View>

        {/* Session Depth: stacked 1 col, 140px charts */}
        <Animated.View entering={FadeInDown.delay(240).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <MobSectionLabel title="Session Duration Distribution" colors={colors} />
            <GroupedBarChart
              data={(data?.sessionDepth.buckets ?? []).map((b) => ({
                label: b.label,
                values: [b.startups, b.investors],
              }))}
              seriesColors={['#0D9488', '#3B82F6']}
              seriesLabels={['Startups', 'Investors']}
              height={140}
            />
            {data?.sessionDepth.insightText && (
              <View
                className="flex-row items-start gap-2 mt-3 px-3 py-2.5 rounded-lg"
                style={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}
              >
                <AlertTriangle size={12} color="#D97706" style={{ marginTop: 1 }} />
                <Text style={{ color: '#92400E' }} className="text-xs flex-1 leading-4">
                  {data.sessionDepth.insightText}
                </Text>
              </View>
            )}
          </MobCard>
        </Animated.View>
        <Animated.View entering={FadeInDown.delay(260).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <MobSectionLabel title="Sessions per Week" colors={colors} />
            <GroupedBarChart
              data={(data?.sessionDepth.sessionsPerWeek ?? []).map((w) => ({
                label: w.week,
                values: [w.startups, w.investors],
              }))}
              seriesColors={['#0D9488', '#3B82F6']}
              seriesLabels={['Startups', 'Investors']}
              height={140}
            />
          </MobCard>
        </Animated.View>

        {/* Search Analytics: stacked sections, top 6 filters */}
        <Animated.View entering={FadeInDown.delay(300).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <MobSectionLabel title="Search & Discovery" colors={colors} />
            {/* KPI row */}
            <View className="flex-row gap-2 mb-3">
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                  {(data?.searchAnalytics.searchToViewRate ?? 0).toFixed(1)}%
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">Search→View</Text>
              </View>
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: colors.surface.secondary }}>
                <Text style={{ color: '#EF4444' }} className="text-base font-bold">
                  {(data?.searchAnalytics.zeroResultsRate ?? 0).toFixed(1)}%
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">Zero Results</Text>
              </View>
            </View>
            {/* Top filters */}
            <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
              Top Search Filters
            </Text>
            <View className="gap-1.5 mb-3">
              {(data?.searchAnalytics.topFilters ?? []).slice(0, 6).map((f, i) => (
                <View key={`filter-${i}`} className="flex-row items-center gap-2">
                  <Text style={{ color: colors.text.secondary, width: 80 }} className="text-xs" numberOfLines={1}>
                    {f.filter}
                  </Text>
                  <View className="flex-1 h-3 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                    <View
                      className="h-full rounded"
                      style={{ width: `${(f.uses / maxFilterUses) * 100}%`, backgroundColor: '#3B82F630' }}
                    />
                  </View>
                  <Text style={{ color: colors.text.primary, width: 28 }} className="text-xs font-bold text-right">
                    {f.uses}
                  </Text>
                </View>
              ))}
            </View>
            {/* Top categories */}
            <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
              Most Viewed Categories
            </Text>
            <View className="gap-1.5">
              {(data?.searchAnalytics.topCategories ?? []).map((c, i) => (
                <View key={`category-${i}`} className="flex-row items-center gap-2">
                  <Text style={{ color: colors.text.secondary, width: 80 }} className="text-xs" numberOfLines={1}>
                    {c.category}
                  </Text>
                  <View className="flex-1 h-3 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                    <View
                      className="h-full rounded"
                      style={{
                        width: `${(c.views / (data?.searchAnalytics.topCategories[0]?.views ?? 1)) * 100}%`,
                        backgroundColor: '#8B5CF630',
                      }}
                    />
                  </View>
                  <Text style={{ color: colors.text.primary, width: 28 }} className="text-xs font-bold text-right">
                    {c.views}
                  </Text>
                </View>
              ))}
            </View>
          </MobCard>
        </Animated.View>

        {/* Content Engagement: single column */}
        <Animated.View entering={FadeInDown.delay(340).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <MobSectionLabel title="Content Engagement" colors={colors} />
            <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
              Profile Completeness Distribution
            </Text>
            <MiniBarChart
              data={(data?.contentEngagement.completenessDistribution ?? []).map((b) => ({
                label: b.bucket,
                value: b.count,
              }))}
              color="#0D9488"
              height={120}
            />
            <View
              className="flex-row items-center gap-2 mt-3 px-3 py-2 rounded-lg"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.08)' }}
            >
              <TrendingUp size={12} color="#16A34A" />
              <Text style={{ color: '#166534' }} className="text-xs flex-1">
                Startups with {'>'}78% completeness get {data?.contentEngagement.completenessVsViews.avgViews.toFixed(1)}x more views
              </Text>
            </View>
            <View className="flex-row items-center justify-between mt-3 mb-2">
              <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold">
                Most Viewed Sections
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                Doc upload: {(data?.contentEngagement.documentUploadRate ?? 0).toFixed(1)}%
              </Text>
            </View>
            <View className="gap-1.5">
              {(data?.contentEngagement.topSections ?? []).map((s, i) => (
                <View key={`section-${i}`} className="flex-row items-center gap-2">
                  <Text style={{ color: colors.text.secondary, width: 88 }} className="text-xs" numberOfLines={1}>
                    {s.section}
                  </Text>
                  <View className="flex-1 h-3 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                    <View
                      className="h-full rounded"
                      style={{ width: `${(s.views / maxSectionViews) * 100}%`, backgroundColor: '#0D948830' }}
                    />
                  </View>
                  <Text style={{ color: colors.text.primary, width: 28 }} className="text-xs font-bold text-right">
                    {s.views}
                  </Text>
                </View>
              ))}
            </View>
          </MobCard>
        </Animated.View>

        {/* Matching Quality: 2x2 KPI grid + alerts */}
        <Animated.View entering={FadeInDown.delay(380).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <MobSectionLabel title="Matching Quality" colors={colors} />
            <View className="flex-row flex-wrap gap-3 mb-3">
              <MobKpiPill
                icon={<Handshake size={14} color="#0D9488" />}
                label="Matches/Inv/Wk"
                value={(data?.matchingQuality.matchesPerInvestorPerWeek ?? 0).toFixed(1)}
                accentColor="#0D9488"
                colors={colors}
              />
              <MobKpiPill
                icon={<Zap size={14} color="#22C55E" />}
                label="Response Rate"
                value={`${(data?.matchingQuality.responseRate ?? 0).toFixed(1)}%`}
                accentColor="#22C55E"
                colors={colors}
              />
              <MobKpiPill
                icon={<Clock size={14} color="#3B82F6" />}
                label="Avg Response"
                value={`${(data?.matchingQuality.avgResponseTimeHours ?? 0).toFixed(0)}h`}
                accentColor="#3B82F6"
                colors={colors}
              />
              <MobKpiPill
                icon={<Activity size={14} color="#8B5CF6" />}
                label="Conn Quality"
                value={`${data?.matchingQuality.connectionQualityScore ?? 0}/100`}
                accentColor="#8B5CF6"
                colors={colors}
              />
            </View>
            <View
              className="flex-row items-center gap-3 px-4 py-3 rounded-xl mb-2"
              style={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}
            >
              <UserX size={18} color="#D97706" />
              <View className="flex-1">
                <Text style={{ color: '#92400E' }} className="text-xs font-bold">
                  {data?.matchingQuality.unmatchedStartups ?? 0} Unmatched Startups
                </Text>
                <Text style={{ color: '#A16207' }} className="text-xs">
                  Complete profiles with 0 views in 30d
                </Text>
              </View>
            </View>
            <View
              className="flex-row items-center gap-3 px-4 py-3 rounded-xl overflow-hidden"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)' }}
            >
              <Search size={18} color="#DC2626" />
              <View className="flex-1">
                <Text style={{ color: '#991B1B' }} className="text-xs font-bold">
                  {data?.matchingQuality.unmatchedInvestors ?? 0} Unmatched Investors
                </Text>
                <Text style={{ color: '#B91C1C' }} className="text-xs">
                  Searched but shortlisted nothing
                </Text>
              </View>
            </View>
          </MobCard>
        </Animated.View>

        {/* Churn Analysis: stacked, 140px retention curve */}
        <Animated.View entering={FadeInDown.delay(420).duration(400)} className="mb-4">
          <MobCard colors={colors}>
            <MobSectionLabel title="Drop-off & Churn" colors={colors} />
            <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mb-2">
              Retention Curve (% remaining)
            </Text>
            <AreaChart
              data={churnAreaData}
              colors={['#0D9488', '#3B82F6']}
              labels={['Startups', 'Investors']}
              height={140}
            />
            <Text style={{ color: colors.text.secondary }} className="text-xs font-semibold mt-3 mb-2">
              Last Action Before Churn
            </Text>
            <View className="gap-1.5 mb-3">
              {(data?.churnAnalysis.lastActionBeforeChurn ?? []).map((a, i) => (
                <View key={`action-${i}`} className="flex-row items-center gap-2">
                  <Text style={{ color: colors.text.secondary, width: 88 }} className="text-xs" numberOfLines={1}>
                    {a.action}
                  </Text>
                  <View className="flex-1 h-3 rounded overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                    <View
                      className="h-full rounded"
                      style={{ width: `${(a.percentage / maxActionPct) * 100}%`, backgroundColor: '#EF444430' }}
                    />
                  </View>
                  <Text style={{ color: colors.text.primary, width: 28 }} className="text-xs font-bold text-right">
                    {a.percentage}%
                  </Text>
                </View>
              ))}
            </View>
            {/* Reactivation pills */}
            <View className="flex-row gap-2">
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: 'rgba(34, 197, 94, 0.08)' }}>
                <Text style={{ color: '#16A34A' }} className="text-base font-bold">
                  {(data?.churnAnalysis.reactivationRate ?? 0).toFixed(1)}%
                </Text>
                <Text style={{ color: '#166534' }} className="text-xs">Overall</Text>
              </View>
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: 'rgba(13, 148, 136, 0.08)' }}>
                <Text style={{ color: '#0D9488' }} className="text-base font-bold">
                  {(data?.churnAnalysis.reactivationRateByType.startup ?? 0).toFixed(1)}%
                </Text>
                <Text style={{ color: '#0D9488' }} className="text-xs">Startup</Text>
              </View>
              <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: 'rgba(59, 130, 246, 0.08)' }}>
                <Text style={{ color: '#3B82F6' }} className="text-base font-bold">
                  {(data?.churnAnalysis.reactivationRateByType.investor ?? 0).toFixed(1)}%
                </Text>
                <Text style={{ color: '#3B82F6' }} className="text-xs">Investor</Text>
              </View>
            </View>
          </MobCard>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
}

function MobCard({
  children,
  colors,
}: {
  children: React.ReactNode;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View
      className="rounded-2xl px-4 py-4"
      style={{ backgroundColor: colors.surface.primary }}
    >
      {children}
    </View>
  );
}

function MobKpiPill({
  icon,
  label,
  value,
  accentColor,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accentColor: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View
      className="flex-1 rounded-2xl px-3 py-3 gap-1"
      style={{ backgroundColor: colors.surface.primary, minWidth: '45%' }}
    >
      <View className="flex-row items-center gap-1.5">
        {icon}
        <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
          {label}
        </Text>
      </View>
      <Text style={{ color: accentColor }} className="text-xl font-bold" numberOfLines={1}>
        {value}
      </Text>
    </View>
  );
}

function MobSectionLabel({
  title,
  colors,
}: {
  title: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <Text style={{ color: colors.text.primary }} className="text-base font-bold mb-2" numberOfLines={2}>
      {title}
    </Text>
  );
}

// ---- Web helper components ----

function SectionHeader({
  title,
  subtitle,
  colors,
}: {
  title: string;
  subtitle: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View>
      <Text style={{ color: colors.text.primary }} className="text-base font-bold">
        {title}
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5">
        {subtitle}
      </Text>
    </View>
  );
}

function DurationSplit({
  label,
  value,
  unit,
  colors,
}: {
  label: string;
  value: number;
  unit: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    // A real basis is what lets the parent row wrap at all: with the default
    // `auto` the four items just squeeze. 120 keeps all four on one line on a
    // wide card (4x120 = 480) and drops them to a 2x2 grid once the viewport
    // cannot hold that, with flexGrow soaking up the leftover space.
    <View className="items-center" style={{ flexBasis: 120, flexGrow: 1 }}>
      <Text style={{ color: colors.text.primary }} className="text-lg font-bold" numberOfLines={1}>
        {value.toFixed(1)}
      </Text>
      <Text style={{ color: colors.text.tertiary }} className="text-[10px]" numberOfLines={1}>
        {label} ({unit})
      </Text>
    </View>
  );
}

function MiniKpi({
  icon,
  label,
  value,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View
      className="flex-row items-center gap-2 rounded-xl px-3 py-2.5"
      style={{ backgroundColor: colors.surface.secondary }}
    >
      {icon}
      <View className="flex-1">
        <Text style={{ color: colors.text.primary }} className="text-sm font-bold" numberOfLines={1}>
          {value}
        </Text>
        <Text style={{ color: colors.text.tertiary }} className="text-[10px]">
          {label}
        </Text>
      </View>
    </View>
  );
}
